import React, { Fragment, useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { H5, Breadcrumbs } from "../../AbstractElements";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Media,
  Modal,
  ModalBody,
  Row,
} from "reactstrap";
import FooterCard from "../../Components/Forms/FormControl/Common/FooterCard";
import { titleCase } from "../../utils/case";
import { getLotById, updateLot, getLotSelectionData, deleteLotPhoto } from "../../api/lot.api";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";

import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import {
  EditorState,
  convertFromHTML,
  ContentState,
  convertToRaw,
} from "draft-js";
import draftToHtml from "draftjs-to-html";
import { generateTimeOptions, to12Hour } from "../../utils/datetime";
import { Typeahead } from "react-bootstrap-typeahead";
import "react-bootstrap-typeahead/css/Typeahead.css";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";


const LotEdit = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const formRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [selectionLoading, setSelectionLoading] = useState(false);
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [selectionData, setSelectionData] = useState({
    locations: [],
    addOns: [],
  });
  const [editorState, setEditorState] = useState(() =>
    EditorState.createEmpty()
  );
  const chargedByOptions = [
    "hourly",
    "nightly",
    "daily",
    "monthly",
    "yearly",
    "flat_fee",
  ];
  const defaultPricingStructures = chargedByOptions.map((chargedBy) => ({
    chargedBy,
    nonPeakAmount: "",
    peakAmount: "",
  }));
  const [form, setForm] = useState({
    name: "",
    location: "",
    description: "",
    capacity: "",
    configurationType: "standard",
    checkInTime: "14:00",
    checkOutTime: "12:00",
    allowAdditionalGuests: false,
    addOnRequired: false,
    expiredAt: null,
    type: "permanent",
    addOns: [],
    remark: "",
    requiresPayment: false,
    requiresApproval: false,
    allowPublicBooking: false,
    pricingStructures: defaultPricingStructures,
    photos: [],
    existingPhotos: [],
    minLeadTimeValue: "",
    minLeadTimeType: "",
    maxLeadTimeValue: "",
    maxLeadTimeType: "",
    operatingHours: [{ day: "", startTime: "", endTime: "" }],
  });
  const isExpired = (() => {
    if (!form.expiredAt) return false;

    const today = new Date();
    const todayDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

    return form.expiredAt < todayDate;
  })();

  const parseJsonField = (value, fallback = null) => {
    if (!value) return fallback;
    if (typeof value === "string") {
      try {
        return JSON.parse(value);
      } catch {
        return fallback;
      }
    }
    return value;
  };
  const parsePricing = (pricing) => {
    if (!pricing) return [];
    if (typeof pricing === "string") {
      try {
        return JSON.parse(pricing);
      } catch {
        return [];
      }
    }
    return Array.isArray(pricing) ? pricing : [];
  };
  const mapPricingToForm = (pricing) => {
    const parsedPricing = parsePricing(pricing);

    return chargedByOptions.map((chargedBy) => {
      const nonPeak = parsedPricing.find(
        (item) =>
          item.chargedBy === chargedBy &&
          item.season === "non_peak"
      );

      const peak = parsedPricing.find(
        (item) =>
          item.chargedBy === chargedBy &&
          item.season === "peak"
      );

      return {
        chargedBy,
        nonPeakAmount: nonPeak?.amount ?? "",
        peakAmount: peak?.amount ?? "",
      };
    });
  };

  const loadSelectionData = async () => {
    try {
      setSelectionLoading(true);

      const data = await getLotSelectionData();

      setSelectionData({
        locations: data.locations || [],
        addOns: data.addOns || [],
      });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSelectionLoading(false);
    }
  };

  const fetchLot = async () => {
    try {
      const data = await getLotById(id);

      const parsedMinLeadTime = parseJsonField(data.minLeadTime);
      const parsedMaxLeadTime = parseJsonField(data.maxLeadTime);
      const blocksFromHTML = convertFromHTML(data.remark || "");
      const contentState = ContentState.createFromBlockArray(
        blocksFromHTML.contentBlocks,
        blocksFromHTML.entityMap
      );

      setEditorState(EditorState.createWithContent(contentState));
      const configurationType = data.isDayTrip ? "day_trip" : data.isEvent ? "event" : "standard";

      setForm({
        name: data.name || "",
        location: data.location || "",
        description: data.description || "",
        capacity: data.capacity ?? "",
        configurationType,
        checkInTime: data.checkInTime?.slice(0, 5) || (configurationType === "standard" ? "14:00" : "07:00"),
        checkOutTime: data.checkOutTime?.slice(0, 5) || (configurationType === "standard" ? "12:00" : "19:00"),
        allowAdditionalGuests: data.allowAdditionalGuests ?? false,
        addOnRequired: data.addOnRequired ?? false,
        expiredAt: data.expiredAt ?? null,
        type: data.type || "permanent",
        addOns:
          data.lotAddOns?.map((item) => ({
            addOnId: item.addOnId,
            minQty: item.minQty ?? "",
            maxQty: item.maxQty ?? "",
            isEventPass: !!item.isEventPass,
          })) || [],

        remark: data.remark || "",
        requiresPayment: data.requiresPayment ?? false,
        requiresApproval: data.requiresApproval ?? false,
        allowPublicBooking: data.allowPublicBooking ?? false,
        pricingStructures: mapPricingToForm(data.pricing),
        photos: [],
        existingPhotos: data.photos || [],

        minLeadTimeValue: parsedMinLeadTime?.value || "",
        minLeadTimeType: parsedMinLeadTime?.type || "",
        maxLeadTimeValue: parsedMaxLeadTime?.value || "",
        maxLeadTimeType: parsedMaxLeadTime?.type || "",

        operatingHours:
          data.operatingHours?.length > 0
            ? data.operatingHours.map((oh) => ({
                day: oh.day,
                startTime: oh.startTime?.slice(0, 5),
                endTime: oh.endTime?.slice(0, 5),
              }))
            : [{ day: "", startTime: "", endTime: "" }],
      });
    } catch (err) {
      toast.error(err.message);
    }
  };

  useEffect(() => {
    loadSelectionData();
    fetchLot();
  }, [id]);

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleConfigurationTypeChange = (value) => {
    let checkInTime = "14:00";
    let checkOutTime = "12:00";

    if (value === "day_trip" || value === "event") {
      checkInTime = "07:00";
      checkOutTime = "19:00";
    }

    setForm((prev) => ({
      ...prev,
      configurationType: value,
      checkInTime,
      checkOutTime,
    }));
  };

  const isPricingSelected = (chargedBy) => {
    const pricing = form.pricingStructures.find(
      (item) => item.chargedBy === chargedBy
    );

    return !!(
      pricing?.nonPeakAmount ||
      pricing?.peakAmount
    );
  };

  const isPricingDisabled = (chargedBy) => {
    const flatSelected = isPricingSelected("flat_fee");
    const dailySelected = isPricingSelected("daily");
    const nightlySelected = isPricingSelected("nightly");

    // Flat rate means no other pricing method can apply
    if (flatSelected && chargedBy !== "flat_fee") {
      return true;
    }

    // Per day and per night are mutually exclusive with
    // each other and with flat rate
    if (
      dailySelected &&
      (chargedBy === "flat_fee" || chargedBy === "nightly")
    ) {
      return true;
    }

    if (
      nightlySelected &&
      (chargedBy === "flat_fee" || chargedBy === "daily")
    ) {
      return true;
    }

    return false;
  };

  const handlePricingChange = (chargedBy, field, value) => {
    setForm((prev) => {
      const flatSelected =
        chargedBy === "flat_fee" &&
        (field === "nonPeakAmount" || field === "peakAmount") &&
        value !== "";

      const dailySelected =
        chargedBy === "daily" &&
        (field === "nonPeakAmount" || field === "peakAmount") &&
        value !== "";

      const nightlySelected =
        chargedBy === "nightly" &&
        (field === "nonPeakAmount" || field === "peakAmount") &&
        value !== "";

      let pricingStructures = prev.pricingStructures.map((item) =>
        item.chargedBy === chargedBy
          ? { ...item, [field]: value }
          : item
      );

      // If Flat Rate is being selected, clear all other pricing methods
      if (flatSelected) {
        pricingStructures = pricingStructures.map((item) =>
          item.chargedBy !== "flat_fee"
            ? {
                ...item,
                nonPeakAmount: "",
                peakAmount: "",
              }
            : item
        );
      }

      // If Per Day is being selected, clear Flat Rate and Per Night
      if (dailySelected) {
        pricingStructures = pricingStructures.map((item) =>
          ["flat_fee", "nightly"].includes(item.chargedBy)
            ? {
                ...item,
                nonPeakAmount: "",
                peakAmount: "",
              }
            : item
        );
      }

      // If Per Night is being selected, clear Flat Rate and Per Day
      if (nightlySelected) {
        pricingStructures = pricingStructures.map((item) =>
          ["flat_fee", "daily"].includes(item.chargedBy)
            ? {
                ...item,
                nonPeakAmount: "",
                peakAmount: "",
              }
            : item
        );
      }

      return {
        ...prev,
        pricingStructures,
      };
    });
  };

  const onEditorStateChange = (state) => {
    setEditorState(state);
  };

  const handleAddOnChange = (addOnId, checked) => {
    setForm((prev) => ({
      ...prev,
      addOns: checked
        ? [
            ...prev.addOns,
            {
              addOnId,
              minQty: "",
              maxQty: "",
              isEventPass: false,
            },
          ]
        : prev.addOns.filter((item) => item.addOnId !== addOnId),
    }));
  };

  const handleAddOnQtyChange = (addOnId, field, value) => {
    setForm((prev) => ({
      ...prev,
      addOns: prev.addOns.map((item) =>
        item.addOnId === addOnId
          ? {
              ...item,
              [field]: value,
            }
          : item
      ),
    }));
  };

  const getSelectedAddOn = (addOnId) => {
    return form.addOns.find((item) => item.addOnId === addOnId);
  };

  const handleOperatingHourChange = (index, field, value) => {
    const updated = [...form.operatingHours];
    updated[index][field] = value;

    setForm((prev) => ({
      ...prev,
      operatingHours: updated,
    }));
  };

  const addOperatingHour = () => {
    setForm((prev) => ({
      ...prev,
      operatingHours: [
        ...prev.operatingHours,
        { day: "", startTime: "", endTime: "" },
      ],
    }));
  };

  const removeOperatingHour = (index) => {
    const updated = form.operatingHours.filter((_, i) => i !== index);

    setForm((prev) => ({
      ...prev,
      operatingHours: updated,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isExpired) {
      return toast.error("Expired lots cannot be edited");
    }

    try {
      if (form.requiresPayment) {
        const hasAtLeastOnePricing = form.pricingStructures.some(
          (item) => item.nonPeakAmount || item.peakAmount
        );

        if (!hasAtLeastOnePricing) {
          return toast.error("At least one pricing structure is required");
        }
      }

      const validOperatingHours = form.operatingHours.filter(
        (oh) => oh.day && oh.startTime && oh.endTime
      );

      const remarkHtml = draftToHtml(
        convertToRaw(editorState.getCurrentContent())
      );

      const minLeadTime = form.minLeadTimeValue
        ? {
            value: Number(form.minLeadTimeValue),
            type: form.minLeadTimeType,
          }
        : null;

      const maxLeadTime = form.maxLeadTimeValue
        ? {
            value: Number(form.maxLeadTimeValue),
            type: form.maxLeadTimeType,
          }
        : null;

      const payload = {
        name: form.name,
        location: form.location,
        capacity: form.capacity ? Number(form.capacity) : null,
        configurationType: form.configurationType,
        checkInTime: form.checkInTime,
        checkOutTime: form.checkOutTime,
        allowAdditionalGuests: form.allowAdditionalGuests,
        addOnRequired: form.addOnRequired,
        type: form.type,
        description: form.description,
        addOns: form.addOns.map((item) => ({
          addOnId: item.addOnId,
          minQty: item.minQty !== "" ? Number(item.minQty) : 0,
          maxQty: item.maxQty !== "" ? Number(item.maxQty) : null,
          isEventPass: !!item.isEventPass,
        })),
        remark: remarkHtml,
        requiresPayment: form.requiresPayment,
        requiresApproval: form.requiresApproval,
        allowPublicBooking: form.allowPublicBooking,
        minLeadTime,
        maxLeadTime,
        operatingHours: validOperatingHours,
        photos: form.photos,

        pricing: form.requiresPayment
          ? form.pricingStructures.flatMap((item) => {
              const prices = [];

              if (item.nonPeakAmount) {
                prices.push({
                  season: "non_peak",
                  amount: Number(item.nonPeakAmount),
                  chargedBy: item.chargedBy,
                });
              }

              if (item.peakAmount) {
                prices.push({
                  season: "peak",
                  amount: Number(item.peakAmount),
                  chargedBy: item.chargedBy,
                });
              }

              return prices;
            })
          : [],
      };

      setLoading(true);

      await updateLot(id, payload);

      toast.success("Lot updated successfully");
      navigate(`/lots/view/${id}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const triggerSubmit = () => {
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  };

  return (
    <Fragment>
      <Breadcrumbs
        parent={titleCase(t("lot"))}
        title={titleCase(t("edit lot"))}
        mainTitle={titleCase(t("edit lot"))}
      />

      <Container fluid>
        <Row>
          <Col>
            {isExpired && (
              <div className="alert alert-danger mb-4">
                <strong>{titleCase(t("expired"))}</strong> — {t("this lot has expired and can no longer be edited")}
              </div>
            )}

            <div
              style={
                isExpired
                  ? {
                      pointerEvents: "none",
                      opacity: 0.6,
                    }
                  : undefined
              }
            >
            <Card className="mb-4">
              <CardBody>
                <Form innerRef={formRef} onSubmit={handleSubmit}>
                  <Row className="mb-3">
                    <Col>
                      {form.existingPhotos?.length ? (
                        <Swiper
                          modules={[Navigation, Pagination]}
                          navigation
                          pagination={{ clickable: true }}
                          spaceBetween={16}
                          slidesPerView={5}
                          breakpoints={{
                            0: { slidesPerView: 1 },
                            576: { slidesPerView: 2 },
                            768: { slidesPerView: 3 },
                            992: { slidesPerView: 4 },
                            1200: { slidesPerView: 5 },
                          }}
                        >
                          {form.existingPhotos.map((photo) => (
                            <SwiperSlide key={photo}>
                              <div className="border rounded p-2">
                                <img
                                  src={`${process.env.REACT_APP_FILES_URL}/${photo}`}
                                  alt=""
                                  onClick={() => setPreviewPhoto(photo)}
                                  style={{
                                    width: "100%",
                                    height: 180,
                                    objectFit: "cover",
                                    borderRadius: 8,
                                    cursor: "zoom-in",
                                  }}
                                />

                                <button
                                  className="btn btn-danger btn-sm mt-2 w-100"
                                  type="button"
                                  onClick={async () => {
                                    if (!window.confirm("Delete this image?")) return;

                                    const fileName = photo.split("/").pop();
                                    const res = await deleteLotPhoto(
                                      id,
                                      fileName
                                    );

                                    setForm((prev) => ({
                                      ...prev,
                                      existingPhotos: res.photos || [],
                                    }));

                                    toast.success("Image deleted.");
                                  }}
                                >
                                  Delete
                                </button>
                              </div>
                            </SwiperSlide>
                          ))}
                        </Swiper>
                      ) : (
                        <p>No images</p>
                      )}
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md="12" className="d-flex align-items-center">
                      <FormGroup className="mb-0 w-100">
                        <Label>{titleCase(t("add images"))}</Label>
                        <Input
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={(e) => handleChange("photos", Array.from(e.target.files))}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md="4">
                      <FormGroup>
                        <Label>{titleCase(t("expired at"))}</Label>
                        <Input
                          type="date"
                          value={form.expiredAt || ""}
                          disabled
                          readOnly
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md="8">
                      <FormGroup>
                        <Label>{titleCase(t("lot name"))}</Label>
                        <Input
                          type="text"
                          value={form.name}
                          onChange={(e) =>
                            handleChange("name", e.target.value)
                          }
                          required
                        />
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Label>
                          {titleCase(t("configuration type"))}
                        </Label>

                        <Input
                          type="select"
                          value={form.configurationType}
                          onChange={(e) =>
                            handleConfigurationTypeChange(
                              e.target.value
                            )
                          }
                        >
                          <option value="standard">
                            {titleCase(t("standard"))}
                          </option>

                          <option value="day_trip">
                            {titleCase(t("day trip"))}
                          </option>

                          <option value="event">
                            {titleCase(t("event"))}
                          </option>
                        </Input>

                        <small className="text-muted d-block mt-1">
                          {form.configurationType === "standard"
                            ? "Standard overnight booking"
                            : form.configurationType === "day_trip"
                            ? "Same-day booking"
                            : "Fixed-date booking"}
                        </small>
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md="3">
                      <FormGroup>
                        <Label>{titleCase(t("capacity"))}</Label>
                        <Input
                          type="number"
                          min="0"
                          value={form.capacity}
                          onChange={(e) =>
                            handleChange("capacity", e.target.value)
                          }
                        />
                      </FormGroup>
                    </Col>

                    <Col md="3" className="d-flex align-items-end">
                      <Media className="mb-3 align-items-center">
                        <Label className="switch m-r-10 mb-0">
                          <Input
                            type="checkbox"
                            checked={form.allowAdditionalGuests}
                            onChange={(e) =>
                              handleChange("allowAdditionalGuests", e.target.checked)
                            }
                          />
                          <span className="switch-state"></span>
                        </Label>
                        <Label className="mb-0">
                          {titleCase(t("allow additional guests"))}
                        </Label>
                      </Media>
                    </Col>

                    <Col md="2" className="d-flex align-items-end">
                      <Media className="mb-3 align-items-center">
                        <Label className="switch m-r-10 mb-0">
                          <Input
                            type="checkbox"
                            checked={form.addOnRequired}
                            onChange={(e) =>
                              handleChange("addOnRequired", e.target.checked)
                            }
                          />
                          <span className="switch-state"></span>
                        </Label>
                        <Label className="mb-0">
                          {titleCase(t("add on required"))}
                        </Label>
                      </Media>
                    </Col>

                    <Col md="4">
                      <FormGroup>
                        <Label>{titleCase(t("type"))}</Label>
                        <Input
                          type="select"
                          value={form.type}
                          onChange={(e) =>
                            handleChange("type", e.target.value)
                          }
                        >
                          <option value="permanent">
                            {titleCase(t("permanent"))}
                          </option>
                          <option value="temporary">
                            {titleCase(t("temporary"))}
                          </option>
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>

                  <FormGroup>
                    <Label>{titleCase(t("location"))}</Label>

                    <Typeahead
                      id="lot-location-typeahead"
                      allowNew
                      clearButton
                      multiple={false}
                      options={selectionData.locations}
                      selected={form.location ? [form.location] : []}
                      placeholder={titleCase(t("enter or select location"))}
                      newSelectionPrefix={`${titleCase(t("add new location"))}: `}
                      onChange={(selected) => {
                        const value =
                          selected.length > 0
                            ? typeof selected[0] === "string"
                              ? selected[0]
                              : selected[0].label
                            : "";

                        handleChange("location", value);
                      }}
                      onInputChange={(text) => {
                        handleChange("location", text);
                      }}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("description"))}</Label>
                    <Input
                      type="textarea"
                      value={form.description}
                      onChange={(e) =>
                        handleChange("description", e.target.value)
                      }
                    />
                  </FormGroup>

                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label>
                          {titleCase(t("check in time"))}
                        </Label>

                        <Input
                          type="time"
                          value={form.checkInTime}
                          onChange={(e) =>
                            handleChange(
                              "checkInTime",
                              e.target.value
                            )
                          }
                          required
                        />
                      </FormGroup>
                    </Col>

                    <Col md="6">
                      <FormGroup>
                        <Label>
                          {titleCase(t("check out time"))}
                        </Label>

                        <Input
                          type="time"
                          value={form.checkOutTime}
                          onChange={(e) =>
                            handleChange(
                              "checkOutTime",
                              e.target.value
                            )
                          }
                          required
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            </Card>

            <Card className="mb-4">
              <CardHeader>
                <H5>{titleCase(t("costs"))}</H5>
              </CardHeader>

              <CardBody>
                <Media className="mb-3 align-items-center">
                  <Label className="switch m-r-10 mb-0">
                    <Input
                      type="checkbox"
                      checked={form.requiresPayment}
                      onChange={(e) =>
                        handleChange("requiresPayment", e.target.checked)
                      }
                    />
                    <span className="switch-state"></span>
                  </Label>

                  <Label className="mb-0">
                    {titleCase(t("requires payment"))}
                  </Label>
                </Media>

                {form.requiresPayment && (
                  <>
                    <div className="mb-3">
                      <Label className="fw-bold">
                        Payment Configuration
                      </Label>

                      <small className="d-block text-muted">
                        Enter a price to select a pricing method. Uncheck a selected pricing method to remove it.
                      </small>
                    </div>

                    {form.pricingStructures.map((pricing) => {
                      const selected = isPricingSelected(pricing.chargedBy);
                      const disabled = isPricingDisabled(pricing.chargedBy);

                      return (
                        <div
                          key={pricing.chargedBy}
                          className={`border rounded p-3 mb-3 ${
                            disabled ? "bg-light" : ""
                          }`}
                        >
                          <Row className="align-items-center">
                            <Col md="4">
                              <div className="checkbox checkbox-primary">
                                <Input
                                  id={`pricing-${pricing.chargedBy}`}
                                  type="checkbox"
                                  checked={selected}
                                  disabled={disabled}
                                  onChange={(e) => {
                                    if (!e.target.checked) {
                                      handlePricingChange(
                                        pricing.chargedBy,
                                        "nonPeakAmount",
                                        ""
                                      );

                                      handlePricingChange(
                                        pricing.chargedBy,
                                        "peakAmount",
                                        ""
                                      );
                                    }
                                  }}
                                />

                                <Label
                                  for={`pricing-${pricing.chargedBy}`}
                                  className={`mb-0 ${
                                    disabled ? "text-muted" : ""
                                  }`}
                                >
                                  <strong>
                                    {titleCase(
                                      t(
                                        pricing.chargedBy.replace(/_/g, " ")
                                      )
                                    )}
                                  </strong>
                                </Label>
                              </div>

                              {disabled && (
                                <small className="text-muted d-block mt-1 ms-4">
                                  Not available with the selected pricing method.
                                </small>
                              )}
                            </Col>

                            <Col md="4">
                              <FormGroup className="mb-0">
                                <Label>
                                  {titleCase(t("standard price"))}
                                </Label>

                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  disabled={disabled}
                                  value={pricing.nonPeakAmount}
                                  onChange={(e) =>
                                    handlePricingChange(
                                      pricing.chargedBy,
                                      "nonPeakAmount",
                                      e.target.value
                                    )
                                  }
                                  placeholder="0.00"
                                />
                              </FormGroup>
                            </Col>

                            <Col md="4">
                              <FormGroup className="mb-0">
                                <Label>
                                  {titleCase(t("peak season price"))}
                                </Label>

                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  disabled={disabled}
                                  value={pricing.peakAmount}
                                  onChange={(e) =>
                                    handlePricingChange(
                                      pricing.chargedBy,
                                      "peakAmount",
                                      e.target.value
                                    )
                                  }
                                  placeholder="0.00"
                                />
                              </FormGroup>
                            </Col>
                          </Row>
                        </div>
                      );
                    })}
                  </>
                )}
              </CardBody>
            </Card>

            <Card className="mb-4">
              <CardHeader>
                <H5>{titleCase(t("booking settings"))}</H5>
              </CardHeader>

              <CardBody>
                <Row className="mb-3">
                  <Col md="6">
                    <Media className="align-items-center">
                      <Label className="switch m-r-10 mb-0">
                        <Input
                          type="checkbox"
                          checked={form.allowPublicBooking}
                          onChange={(e) =>
                            handleChange(
                              "allowPublicBooking",
                              e.target.checked
                            )
                          }
                        />
                        <span className="switch-state"></span>
                      </Label>

                      <Label className="mb-0">
                        {titleCase(t("allow public booking"))}
                      </Label>
                    </Media>
                  </Col>

                  <Col md="6">
                    <Media className="align-items-center">
                      <Label className="switch m-r-10 mb-0">
                        <Input
                          type="checkbox"
                          checked={form.requiresApproval}
                          onChange={(e) =>
                            handleChange(
                              "requiresApproval",
                              e.target.checked
                            )
                          }
                        />
                        <span className="switch-state"></span>
                      </Label>

                      <Label className="mb-0">
                        {titleCase(t("requires approval"))}
                      </Label>
                    </Media>
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    <Label>{titleCase(t("min lead time"))}</Label>
                    <Row>
                      <Col md="6">
                        <Input
                          type="number"
                          min="0"
                          placeholder={titleCase(t("value"))}
                          value={form.minLeadTimeValue || ""}
                          onChange={(e) =>
                            handleChange(
                              "minLeadTimeValue",
                              e.target.value
                            )
                          }
                        />
                      </Col>

                      <Col md="6">
                        <Input
                          type="select"
                          value={form.minLeadTimeType || ""}
                          onChange={(e) =>
                            handleChange(
                              "minLeadTimeType",
                              e.target.value
                            )
                          }
                        >
                          <option value="">
                            {titleCase(t("type"))}
                          </option>
                          <option value="day">
                            {titleCase(t("day"))}
                          </option>
                          <option value="month">
                            {titleCase(t("month"))}
                          </option>
                          <option value="year">
                            {titleCase(t("year"))}
                          </option>
                        </Input>
                      </Col>
                    </Row>

                    <small className="text-muted">
                      {t("min lead time explanation")}
                    </small>
                  </Col>

                  <Col md="6">
                    <Label>{titleCase(t("max lead time"))}</Label>
                    <Row>
                      <Col md="6">
                        <Input
                          type="number"
                          min="0"
                          placeholder={titleCase(t("value"))}
                          value={form.maxLeadTimeValue || ""}
                          onChange={(e) =>
                            handleChange(
                              "maxLeadTimeValue",
                              e.target.value
                            )
                          }
                        />
                      </Col>

                      <Col md="6">
                        <Input
                          type="select"
                          value={form.maxLeadTimeType || ""}
                          onChange={(e) =>
                            handleChange(
                              "maxLeadTimeType",
                              e.target.value
                            )
                          }
                        >
                          <option value="">
                            {titleCase(t("type"))}
                          </option>
                          <option value="day">
                            {titleCase(t("day"))}
                          </option>
                          <option value="month">
                            {titleCase(t("month"))}
                          </option>
                          <option value="year">
                            {titleCase(t("year"))}
                          </option>
                          <option value="month_end">
                            {titleCase(t("next n month end"))}
                          </option>
                          <option value="year_end">
                            {titleCase(t("next n year end"))}
                          </option>
                        </Input>
                      </Col>
                    </Row>

                    <small className="text-muted">
                      {t("max lead time explanation")}
                    </small>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <Card className="mb-4">
              <CardHeader>
                <H5>{titleCase(t("add ons"))}</H5>
              </CardHeader>

              <CardBody>
                {selectionLoading ? (
                  <p className="text-muted mb-0">
                    {titleCase(t("loading"))}...
                  </p>
                ) : selectionData.addOns.length === 0 ? (
                  <p className="text-muted mb-0">
                    {titleCase(t("no add ons available"))}
                  </p>
                ) : (
                  Object.entries(
                    selectionData.addOns.reduce((groups, addOn) => {
                      const category = addOn.category || "uncategorized";

                      if (!groups[category]) {
                        groups[category] = [];
                      }

                      groups[category].push(addOn);

                      return groups;
                    }, {})
                  ).map(([category, addOns]) => (
                    <div key={category} className="mb-4">
                      <h6 className="mb-3 border-bottom pb-2">
                        {titleCase(t(category.replace(/_/g, " ")))}
                      </h6>

                      <Row>
                        {addOns.map((addOn) => {
                          const selectedAddOn = getSelectedAddOn(addOn.id);
                          const checkboxId = `add-on-${addOn.id}`;

                          return (
                            <Col md="4" key={addOn.id} className="mb-3">
                              <div className="border rounded p-3 h-100">
                                <div className="d-flex justify-content-between align-items-start mb-3">
                                  <div className="d-flex align-items-start">
                                    {addOn.photo && (
                                      <img
                                        src={`${process.env.REACT_APP_FILES_URL}/${addOn.photo}`}
                                        alt={addOn.name}
                                        style={{
                                          width: 70,
                                          height: 70,
                                          objectFit: "contain",
                                          borderRadius: 8,
                                          marginRight: 12,
                                          border: "1px solid #ddd",
                                        }}
                                      />
                                    )}

                                    <FormGroup className="custom-radio-ml mb-0">
                                      <div className="checkbox checkbox-primary">
                                        <Input
                                          id={checkboxId}
                                          type="checkbox"
                                          checked={!!selectedAddOn}
                                          onChange={(e) => handleAddOnChange(addOn.id, e.target.checked)}
                                        />

                                        <Label for={checkboxId} className="mb-0">
                                          <strong>{addOn.name}</strong>
                                        </Label>
                                      </div>
                                    </FormGroup>
                                  </div>

                                  <span
                                    className={`badge ${
                                      addOn.inventoryType === "asset"
                                        ? "badge-primary"
                                        : addOn.inventoryType === "consumable"
                                          ? "badge-warning"
                                          : "badge-secondary"
                                    }`}
                                  >
                                    {titleCase(t(addOn.inventoryType?.replace(/_/g, " ")))}
                                  </span>
                                </div>

                                {addOn.totalStock !== null &&
                                  addOn.totalStock !== undefined && (
                                    <div className="small mb-3">
                                      {titleCase(t("total stock"))}:{" "}
                                      {addOn.totalStock}
                                    </div>
                                  )}

                                {selectedAddOn && (
                                  <>
                                    <Row>
                                      <Col md="6">
                                        <FormGroup>
                                          <Label>
                                            {titleCase(t("minimum quantity"))}
                                          </Label>
                                          <Input
                                            type="number"
                                            min="0"
                                            value={selectedAddOn.minQty}
                                            onChange={(e) =>
                                              handleAddOnQtyChange(
                                                addOn.id,
                                                "minQty",
                                                e.target.value
                                              )
                                            }
                                            placeholder="0"
                                          />
                                        </FormGroup>
                                      </Col>

                                      <Col md="6">
                                        <FormGroup>
                                          <Label>
                                            {titleCase(t("maximum quantity"))}
                                          </Label>
                                          <Input
                                            type="number"
                                            min="0"
                                            value={selectedAddOn.maxQty}
                                            onChange={(e) =>
                                              handleAddOnQtyChange(
                                                addOn.id,
                                                "maxQty",
                                                e.target.value
                                              )
                                            }
                                            placeholder="Optional"
                                          />
                                        </FormGroup>
                                      </Col>
                                    </Row>

                                    <FormGroup className="mb-0">
                                      <div className="checkbox checkbox-primary">
                                        <Input
                                          id={`event-pass-${addOn.id}`}
                                          type="checkbox"
                                          checked={!!selectedAddOn.isEventPass}
                                          onChange={(e) =>
                                            handleAddOnQtyChange(
                                              addOn.id,
                                              "isEventPass",
                                              e.target.checked
                                            )
                                          }
                                        />

                                        <Label
                                          for={`event-pass-${addOn.id}`}
                                          className="mb-0"
                                        >
                                          {titleCase(t("event pass"))}
                                        </Label>
                                      </div>

                                      <small className="text-muted d-block mt-1">
                                        {t("Treat this add-on as an event ticket")}
                                      </small>
                                    </FormGroup>
                                  </>
                                )}
                              </div>
                            </Col>
                          );
                        })}
                      </Row>
                    </div>
                  ))
                )}
              </CardBody>
            </Card>

            <Card className="mb-4">
              <CardHeader>
                <H5>{titleCase(t("remarks"))}</H5>
              </CardHeader>

              <CardBody>
                <Editor
                  editorState={editorState}
                  wrapperClassName="wrapperClassName"
                  editorClassName="editorClassName"
                  toolbarClassName="toolbarClassName"
                  onEditorStateChange={onEditorStateChange}
                  wrapperStyle={{
                    border: "solid 0.5px #d9d9d9",
                    minHeight: "40vh",
                  }}
                  editorStyle={{
                    paddingLeft: "15px",
                    paddingRight: "15px",
                  }}
                />
              </CardBody>
            </Card>

            <FooterCard
              onSubmit={triggerSubmit}
              onCancel={handleCancel}
              loading={loading}
            />
            </div>
          </Col>
        </Row>
      </Container>
      <Modal
        isOpen={!!previewPhoto}
        toggle={() => setPreviewPhoto(null)}
        centered
        size="xl"
      >
        <ModalBody
          className="p-0 text-center bg-dark"
          style={{ overflow: "hidden" }}
        >
          {previewPhoto && (
            <img
              src={`${process.env.REACT_APP_FILES_URL}/${previewPhoto}`}
              alt=""
              style={{
                maxWidth: "100%",
                maxHeight: "85vh",
                objectFit: "contain",
              }}
            />
          )}
        </ModalBody>
      </Modal>
    </Fragment>
  );
};

export default LotEdit;